/**
 * ClientScopeProvider — derives the actor's "selected client" from the
 * URL (`/app/clients/:clientId/...` or production workspace `?client=`)
 * and surfaces the assignment list / leadership-org-wide list for the switcher.
 *
 * Per `docs/frontend/035-frontend-app-shell-plan.md §3.4`:
 *   - non-leadership: switcher lists `me.client_assignments` only.
 *   - leadership (`owner` / `marketing_manager`): switcher lists every
 *     client visible via `list-clients` (org-wide).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { isApiError } from '../api/client';
import { listClients } from '../api/endpoints';
import type { ClientRow } from '../api/types';
import { useMeContext } from './MeContextProvider';
import { isAgencyProductionOnly, isLeadership } from '../permissions';

export type ClientScopeStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface ClientScopeValue {
  readonly status: ClientScopeStatus;
  readonly clients: ReadonlyArray<ClientRow>;
  readonly selectedClientId: string | null;
  readonly error: unknown;
  readonly leadership: boolean;
  readonly refresh: (client?: ClientRow) => void;
}

const Ctx = createContext<ClientScopeValue | null>(null);
const CLIENTS_CACHE_PREFIX = 'kmi:clients:';

function clientsCacheKey(me: NonNullable<ReturnType<typeof useMeContext>['me']>): string {
  return `${CLIENTS_CACHE_PREFIX}${me.organization?.id ?? 'no-org'}:${me.actor.user_id}`;
}

function readCachedClients(key: string): ReadonlyArray<ClientRow> | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as ReadonlyArray<ClientRow>;
  } catch {
    return null;
  }
}

function writeCachedClients(key: string, nextClients: ReadonlyArray<ClientRow>): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(nextClients));
  } catch {
    // Cache is only used to avoid full-page skeletons on tab restore.
  }
}

export function ClientScopeProvider({ children }: { readonly children: ReactNode }) {
  const { me, status: meStatus } = useMeContext();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const productionWorkspace = me ? isAgencyProductionOnly(me) : false;
  const requestedClientId =
    typeof params.clientId === 'string' && params.clientId.length > 0
      ? params.clientId
      : productionWorkspace
        ? searchParams.get('client')
        : null;

  const leadership = useMemo(() => (me ? isLeadership(me) : false), [me]);

  const [clients, setClients] = useState<ReadonlyArray<ClientRow>>([]);
  const [status, setStatus] = useState<ClientScopeStatus>('idle');
  const [error, setError] = useState<unknown>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const selectedClientId = useMemo(
    () =>
      requestedClientId &&
      clients.some((client) => client.id === requestedClientId && client.status !== 'archived')
        ? requestedClientId
        : null,
    [clients, requestedClientId],
  );

  const refresh = useCallback(
    (client?: ClientRow) => {
      if (client) {
        setClients((prev) => {
          const next = prev.some((row) => row.id === client.id)
            ? prev.map((row) => (row.id === client.id ? client : row))
            : [client, ...prev];
          if (me && leadership) writeCachedClients(clientsCacheKey(me), next);
          return next;
        });
      }
      setRefreshKey((n) => n + 1);
    },
    [me, leadership],
  );

  useEffect(() => {
    if (meStatus !== 'ready' || !me || me.system_admin) {
      setClients([]);
      setStatus('idle');
      return;
    }
    if (!leadership) {
      const fromAssignments = me.client_assignments.map<ClientRow>((a) => ({
        id: a.client_id,
        name: a.client_name,
        status: a.client_status,
        created_at: a.started_at,
        updated_at: a.started_at,
      }));
      setClients(fromAssignments);
      setStatus('ready');
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    const cacheKey = clientsCacheKey(me);
    const cached = readCachedClients(cacheKey);
    if (cached) {
      setClients(cached);
      setStatus('ready');
    } else {
      setStatus('loading');
    }
    setError(null);
    listClients({ signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        setClients(data.clients);
        writeCachedClients(cacheKey, data.clients);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (isApiError(err) && err.code === 'not_found') {
          setClients([]);
          setStatus('ready');
          return;
        }
        setError(err);
        setStatus('error');
      });
    return () => ctrl.abort();
  }, [meStatus, me, leadership, refreshKey]);

  const value = useMemo<ClientScopeValue>(
    () => ({ status, clients, selectedClientId, error, leadership, refresh }),
    [status, clients, selectedClientId, error, leadership, refresh],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useClientScope(): ClientScopeValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useClientScope must be used inside ClientScopeProvider');
  return v;
}
